import { useParams, Link, useNavigate } from "react-router-dom";
import { newsItems } from "@/mocks/newsData";
import { ScrollReveal } from "@/components/base/ScrollAnimations";

const categoryColors: Record<string, string> = {
  publication: "bg-tie-teal/10 text-tie-teal",
  conference: "bg-tie-navy/10 text-tie-navy",
  announcement: "bg-amber-50 text-amber-700",
  milestone: "bg-emerald-50 text-emerald-700",
};

function getYouTubeThumbnail(url: string) {
  let id = "";
  const match = url.match(/[?&]v=([^&]+)/);
  if (match) id = match[1];
  else {
    const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
    if (shortMatch) id = shortMatch[1];
  }
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : "";
}

function isVideoUrl(url: string) {
  return url.includes("youtube.com") || url.includes("youtu.be");
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const article = newsItems.find((n) => n.id === id);

  if (!article) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-tie-grey flex items-center justify-center">
            <i className="ri-file-search-line text-2xl text-tie-text-muted" />
          </div>
          <h2 className="text-xl font-semibold text-tie-text-primary mb-2">
            Article not found
          </h2>
          <p className="text-sm text-tie-text-secondary mb-6">
            The press release you are looking for does not exist.
          </p>
          <Link
            to="/press-release"
            className="inline-flex items-center text-sm font-medium text-tie-teal hover:underline"
          >
            <i className="ri-arrow-left-line mr-1.5" />
            Back to Press Releases
          </Link>
        </div>
      </div>
    );
  }

  const related = newsItems
    .filter(
      (n) =>
        n.id !== article.id &&
        n.source === "Textile Today" &&
        n.category === article.category
    )
    .slice(0, 3);

  const paragraphs = article.content
    ? article.content.split("\n\n").filter((p) => p.trim())
    : [];

  return (
    <main className="bg-white">
      {/* Breadcrumb */}
      <div className="border-b border-tie-grey-dark">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto py-4">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-tie-text-muted">
              <Link to="/" className="hover:text-tie-teal transition-colors">
                Home
              </Link>
              <i className="ri-arrow-right-s-line text-tie-grey" />
              <Link
                to="/press-release"
                className="hover:text-tie-teal transition-colors"
              >
                Press Release
              </Link>
              <i className="ri-arrow-right-s-line text-tie-grey" />
              <span className="text-tie-text-primary truncate max-w-[200px] md:max-w-sm">
                {article.title}
              </span>
            </nav>
          </div>
        </div>
      </div>

      {/* Hero Image */}
      <div className="relative w-full h-[320px] md:h-[480px] lg:h-[560px] overflow-hidden bg-tie-navy">
        {article.image ? (
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        ) : isVideoUrl(article.externalUrl || "") ? (
          <>
            <img
              src={getYouTubeThumbnail(article.externalUrl || "")}
              alt={article.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-20 h-20 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center cursor-pointer hover:bg-black/60 transition-colors">
                <i className="ri-play-fill text-4xl text-white ml-1" />
              </div>
            </div>
          </>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-tie-navy to-tie-navy/90 flex items-center justify-center">
            <div className="text-center px-4">
              <i className="ri-newspaper-line text-6xl text-white/20 mb-4" />
              <p className="text-lg text-white/40">Textile Innovation Exchange</p>
            </div>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-tie-navy/80 via-tie-navy/20 to-transparent" />

        {/* Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0">
          <div className="w-full px-4 md:px-8 lg:px-12 pb-8 md:pb-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-4">
                <span
                  className={`px-3 py-1.5 rounded-full text-xs font-medium ${categoryColors[article.category]}`}
                >
                  {article.tag}
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs text-white/70">
                  <i className="ri-calendar-line" />
                  {article.date}
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs text-white/70">
                  <i className="ri-time-line" />
                  {article.readTime}
                </span>
              </div>
              <h1 className="font-serif text-2xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
                {article.title}
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <div className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-20">
        <div className="max-w-4xl mx-auto">
          <ScrollReveal>
            <div className="flex items-center gap-3 mb-8 md:mb-10 pb-6 md:pb-8 border-b border-tie-grey-dark">
              <div className="w-10 h-10 rounded-full bg-tie-navy flex items-center justify-center">
                <i className="ri-building-line text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-tie-text-primary">
                  Textile Innovation Exchange
                </p>
                <p className="text-xs text-tie-text-muted">
                  Official Press Release
                </p>
              </div>
            </div>
          </ScrollReveal>

          {/* Body */}
          <ScrollReveal>
            <div className="prose prose-lg max-w-none">
              {paragraphs.length > 0 ? (
                paragraphs.map((para, i) => (
                  <p
                    key={i}
                    className="text-base md:text-lg text-tie-text-secondary leading-relaxed mb-5 md:mb-6"
                  >
                    {para}
                  </p>
                ))
              ) : (
                <p className="text-base md:text-lg text-tie-text-secondary leading-relaxed">
                  {article.excerpt}
                </p>
              )}
            </div>
          </ScrollReveal>

          {/* External Reference */}
          {article.externalUrl && (
            <ScrollReveal>
              <div className="mt-10 md:mt-12 p-4 md:p-5 rounded-xl bg-tie-grey-light border border-tie-grey-dark">
                <p className="text-xs text-tie-text-muted mb-2">
                  Originally published on {article.source || "partner outlet"}
                </p>
                <a
                  href={article.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm font-medium text-tie-teal hover:underline"
                >
                  View original article
                  <i className="ri-external-link-line ml-1.5 text-xs" />
                </a>
              </div>
            </ScrollReveal>
          )}

          {/* Share */}
          <ScrollReveal>
            <div className="mt-10 md:mt-12 pt-6 md:pt-8 border-t border-tie-grey-dark">
              <p className="text-xs font-semibold uppercase tracking-wider text-tie-text-muted mb-4">
                Share this release
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    const url = window.location.href;
                    window.open(
                      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
                      "_blank"
                    );
                  }}
                  className="w-10 h-10 rounded-full bg-tie-navy text-white flex items-center justify-center hover:bg-tie-navy/80 transition-colors cursor-pointer"
                >
                  <i className="ri-linkedin-fill" />
                </button>
                <button
                  onClick={() => {
                    const url = window.location.href;
                    const text = article.title;
                    window.open(
                      `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
                      "_blank"
                    );
                  }}
                  className="w-10 h-10 rounded-full bg-tie-navy text-white flex items-center justify-center hover:bg-tie-navy/80 transition-colors cursor-pointer"
                >
                  <i className="ri-twitter-x-fill" />
                </button>
                <button
                  onClick={() => {
                    const url = window.location.href;
                    window.open(
                      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
                      "_blank"
                    );
                  }}
                  className="w-10 h-10 rounded-full bg-tie-navy text-white flex items-center justify-center hover:bg-tie-navy/80 transition-colors cursor-pointer"
                >
                  <i className="ri-facebook-fill" />
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                  }}
                  className="w-10 h-10 rounded-full bg-white border border-tie-grey-dark text-tie-text-primary flex items-center justify-center hover:bg-tie-grey transition-colors cursor-pointer"
                  title="Copy link"
                >
                  <i className="ri-link" />
                </button>
              </div>
            </div>
          </ScrollReveal>

          {/* Back Button */}
          <ScrollReveal>
            <div className="mt-10 md:mt-12">
              <button
                onClick={() => navigate("/press-release")}
                className="inline-flex items-center text-sm font-medium text-tie-teal hover:underline cursor-pointer"
              >
                <i className="ri-arrow-left-line mr-1.5" />
                Back to all press releases
              </button>
            </div>
          </ScrollReveal>
        </div>
      </div>

      {/* Related Articles */}
      {related.length > 0 && (
        <section className="bg-tie-grey-light py-12 md:py-20">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <ScrollReveal>
                <h3 className="font-serif text-xl md:text-2xl font-bold text-tie-text-primary mb-6 md:mb-8">
                  Related Press Releases
                </h3>
              </ScrollReveal>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                {related.map((item, index) => (
                  <ScrollReveal key={item.id} delay={index * 100}>
                    <Link
                      to={`/press-release/${item.id}`}
                      className="group block bg-white rounded-2xl overflow-hidden border border-tie-grey-dark hover:border-tie-teal/30 transition-all duration-300"
                    >
                      <div className="relative h-40 md:h-44 overflow-hidden">
                        {item.image ? (
                          <img
                            src={item.image}
                            alt={item.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-tie-navy/80 to-tie-navy flex items-center justify-center">
                            <i className="ri-newspaper-line text-2xl text-white/30" />
                          </div>
                        )}
                        <div className="absolute top-3 left-3">
                          <span
                            className={`px-2 py-1 rounded-full text-[10px] font-medium ${categoryColors[item.category]}`}
                          >
                            {item.tag}
                          </span>
                        </div>
                      </div>
                      <div className="p-4 md:p-5">
                        <p className="text-xs text-tie-text-muted mb-2">
                          {item.date}
                        </p>
                        <h4 className="text-sm font-semibold text-tie-text-primary leading-snug group-hover:text-tie-teal transition-colors line-clamp-2">
                          {item.title}
                        </h4>
                      </div>
                    </Link>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </main>
  );
}